import { z } from "zod";
//#region src/llm-credentials.ts
/**
* Org-scoped LLM credential contracts — org-admin key management (S4) and the
* shared per-org seed element (S7). Replaces the retired per-user BYOK
* `llm-credential-client.ts` (#562). Org-LLM-keys initiative.
*
* Wire-truth for two surfaces:
*   - DP shell `/admin/*`  → core-api `GET/PUT/DELETE/POST /v1/admin/llm-credentials*` (S4).
*     Producer: core-api (WS-2). Consumer: shell (WS-5).
*   - CP create-DP seeding → provisioning-svc → core-api `/internal/v1/llm/seed` (S7→S6).
*     Producer of the shared element: this file (WS-1). Consumers: WS-6 (create DTO + wizard),
*     WS-4/WS-6 (sdk-node CreateDataPlaneInput). The S6 wire body itself lives in
*     `@zion/sdk-cp-dp-contracts` (CreateLlmSeedRequest) and reuses LlmProviderSchema from here.
*
* Plaintext keys NEVER appear in any RESPONSE shape here — admin GET/PUT responses carry only
* `{ configured, preview, updatedAt }` (DEC-14; the masking interceptor enforces this server-side).
* Timestamps are ISO-8601 strings (`z.string`), never `z.coerce.date()` — keeps every request body
* JSON-Schema-representable for core-api's OpenAPI generation (repo gotcha).
*/
/** Supported LLM providers — one key per (org, provider). No 'preferred'/'auto' (D1). */
const LlmProviderSchema = z.enum(["openai", "anthropic"]);
/**
* API-key length bounds, mirrored verbatim from the retired per-user DTO (`VALUE_MIN` = 8 /
* `VALUE_MAX` = 512, core-api `users/llm-credentials/llm-credentials.dto.ts`) so the new org
* surface keeps identical paste guards. Charset is intentionally NOT restricted — provider key
* formats vary (`sk-…`, `sk-ant-…`, project keys) and an over-tight charset would reject valid keys.
*/
const LLM_API_KEY_MIN = 8;
const LLM_API_KEY_MAX = 512;
const apiKeySchema = z.string().min(8).max(512);
/**
* Per-provider status returned to the admin UI. NEVER carries plaintext or any encryption-envelope
* field (DEC-14). Unset provider ⇒ `{ configured: false, preview: null, updatedAt: null }`.
*/
const llmCredentialStatusSchema = z.object({
	configured: z.boolean(),
	/** Masked tail of the stored key (e.g. `…AbCd`), or null when unset. */
	preview: z.string().nullable(),
	/** ISO-8601 timestamp of the last set/replace, or null when unset. */
	updatedAt: z.string().nullable()
}).strict();
/** `GET /v1/admin/llm-credentials` response — both providers' status (S4). */
const getOrgLlmCredentialsResponseSchema = z.object({
	openai: llmCredentialStatusSchema,
	anthropic: llmCredentialStatusSchema
}).strict();
/** `PUT /v1/admin/llm-credentials/:provider` body — set/replace the key (S4). */
const setOrgLlmCredentialBodySchema = z.object({ apiKey: apiKeySchema }).strict();
/**
* `POST /v1/admin/llm-credentials/:provider/test` body — test the supplied key, or (apiKey omitted)
* the stored key. Reuses the existing provider testers server-side (DEC-14).
*/
const testOrgLlmCredentialBodySchema = z.object({ apiKey: apiKeySchema.optional() }).strict();
/** Test-action result. `detail` is a human-readable reason, or null on success (S4). */
const testOrgLlmCredentialResponseSchema = z.object({
	ok: z.boolean(),
	detail: z.string().nullable()
}).strict();
/**
* One per-org credential-seed entry on the create-data-plane request (S7). `orgId` must be one of
* the create request's assigned `orgIds` (DEC-15) — that cross-field check belongs to the create
* DTO's own refinement (WS-6), which has the `orgIds` array in scope; this element only guarantees a
* UUID + at least one key. provisioning-svc transforms this flat shape into the S6 internal seed
* body `{ orgId, credentials: [{ provider, apiKey }] }` (CreateLlmSeedRequest, @zion/sdk-cp-dp-contracts)
* when pushing after READY. The CP persists no plaintext (DEC-9).
*/
const llmCredentialSeedSchema = z.object({
	orgId: z.string().uuidv4(),
	openaiApiKey: apiKeySchema.optional(),
	anthropicApiKey: apiKeySchema.optional()
}).strict().refine((v) => v.openaiApiKey !== void 0 || v.anthropicApiKey !== void 0, { message: "at least one of openaiApiKey or anthropicApiKey is required" });
//#endregion
export { LLM_API_KEY_MAX, LLM_API_KEY_MIN, LlmProviderSchema, getOrgLlmCredentialsResponseSchema, llmCredentialSeedSchema, llmCredentialStatusSchema, setOrgLlmCredentialBodySchema, testOrgLlmCredentialBodySchema, testOrgLlmCredentialResponseSchema };

//# sourceMappingURL=llm-credentials.js.map